import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils";
import { Gem } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function Login() {
  const { login, user, setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [mustChange, setMustChange] = useState(null); // user obj if forced change
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");

  useEffect(() => {
    if (user && !user.must_change_password) {
      navigate(["master_admin", "ho_staff"].includes(user.role) ? "/" : "/portal", { replace: true });
    }
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(email, password);
      if (u.must_change_password) {
        setMustChange(u);
        toast.message("Please set a new password before continuing.");
      } else {
        toast.success("Welcome back");
        navigate(["master_admin", "ho_staff"].includes(u.role) ? "/" : "/portal", { replace: true });
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  const submitNewPassword = async () => {
    if (newPw.length < 6) return toast.error("Password must be at least 6 characters");
    if (newPw !== newPw2) return toast.error("Passwords do not match");
    try {
      await api.post("/auth/change-password", { current_password: password, new_password: newPw });
      toast.success("Password updated");
      const updated = { ...mustChange, must_change_password: false };
      setUser(updated);
      setMustChange(null);
      navigate(["master_admin", "ho_staff"].includes(updated.role) ? "/" : "/portal", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#FAF8F3]">
      <div className="hidden md:flex relative bg-[#0D1B2A] overflow-hidden">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1640654694019-b9d3567c0e56?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxuYXZ5JTIwZ29sZCUyMGx1eHVyeSUyMHRleHR1cmV8ZW58MHx8fHwxNzc4NzUyNTAzfDA&ixlib=rb-4.1.0&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0D1B2A] via-[#0D1B2A]/85 to-transparent" />
        <div className="relative z-10 flex flex-col justify-between p-12 lg:p-16 text-white">
          <div className="flex flex-col items-start gap-5">
            <img src="/brand/ashth_logo.png" alt="Ashth" className="h-28 w-28 lg:h-32 lg:w-32 object-contain drop-shadow-[0_4px_24px_rgba(201,168,76,0.35)]" data-testid="login-ashth-logo" />
            <div>
              <div className="font-serif text-4xl lg:text-5xl tracking-[0.35em] text-[#C9A84C]">ASHTH</div>
              <p className="text-[10px] uppercase tracking-[0.35em] text-white/60 mt-2">Ashth Pricing System</p>
            </div>
          </div>
          <div className="max-w-md">
            <Gem className="w-10 h-10 text-[#C9A84C] mb-6" strokeWidth={1} />
            <h1 className="font-serif text-4xl lg:text-5xl leading-tight mb-4">Pricing, refined.</h1>
            <p className="text-white/70 text-sm leading-relaxed">
              Dynamic pricing for fine jewellery. Update the gold rate once — every piece reprices instantly across head office and every franchise.
            </p>
            <p className="text-white/50 text-xs italic mt-6 font-serif tracking-wider">Mine. Not Mined.</p>
          </div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Secure Access</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md">
          <div className="md:hidden mb-10 text-center">
            <img src="/brand/ashth_logo.png" alt="Ashth" className="h-20 w-20 mx-auto mb-3 object-contain" />
            <span className="font-serif text-4xl tracking-[0.3em] text-[#0D1B2A]">ASHTH</span>
            <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mt-2">Ashth Pricing System</p>
          </div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#C9A84C] mb-3">Sign in</p>
          <h2 className="font-serif text-3xl lg:text-4xl text-[#0D1B2A] mb-2">Welcome back</h2>
          <p className="text-slate-500 text-sm mb-10">Enter your credentials to access the system.</p>
          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-white border-[#0D1B2A]/15 h-12 rounded-md"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">Password</label>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-white border-[#0D1B2A]/15 h-12 rounded-md"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-btn"
              className="w-full h-12 bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white tracking-wide rounded-md"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </div>
      </div>

      <Dialog open={!!mustChange} onOpenChange={() => {}}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Set a new password</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">For security, please set a new password before continuing.</p>
          <div className="space-y-3">
            <PasswordInput placeholder="New password" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="new-password" />
            <PasswordInput placeholder="Confirm new password" value={newPw2} onChange={(e) => setNewPw2(e.target.value)} data-testid="confirm-password" />
          </div>
          <Button onClick={submitNewPassword} className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white" data-testid="change-password-submit">
            Update password
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
